import {
  BLOCKS,
  type Document as RichTextDocument,
  type TopLevelBlock,
} from '@contentful/rich-text-types';
import Grid2 from '@mui/material/Unstable_Grid2';
import type React from 'react';

import { makeSections } from '@/components/BlogComponents/BlogRichText/BlogRichText';
import GenericComponent, {
  type SectionContent,
} from '@/components/GenericComponent/GenericComponent';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import RichText from '@/components/RichText/RichText';
import type { Contentful } from '@/contentful';
import useGlobalStyles from '@/styles/global.styles';

import useStyles from './EditorialHeadingParagraph.styles';

export interface EditorialHeadingParagraphContent extends Contentful {
  fields: {
    heading?: string;
    body?: RichTextDocument;
    headingLevel?: 'h2' | 'h3' | 'h4';
    isOnDark?: boolean;
    addSpace?: boolean;
    fullWidthMode?: boolean;
  };
}

interface EditorialHeadingParagraphContentProps {
  content: EditorialHeadingParagraphContent;
}

export default function EditorialHeadingParagraph({
  content: {
    fields: {
      heading,
      body,
      headingLevel,
      isOnDark = true,
      addSpace = false,
      fullWidthMode = false,
    },
  },
}: EditorialHeadingParagraphContentProps): React.JSX.Element {
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  const { classes, cx } = useStyles({ isOnDark });

  const HeadingComponent = headingLevel
    ? (headingLevel as keyof React.JSX.IntrinsicElements)
    : 'h4';

  return (
    <div className={cx(classes.container, addSpace && classes.spacer)}>
      {body?.content &&
        makeSections(body?.content).map(
          (block: TopLevelBlock | RichTextDocument, i: number) => {
            if (block.nodeType === BLOCKS.DOCUMENT) {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                <GridContainer2 key={i} fixed>
                  <Grid2
                    sm={12}
                    md={fullWidthMode ? 12 : 10}
                    mdOffset={fullWidthMode ? 0 : 1}
                  >
                    {heading && i === 0 && (
                      <HeadingComponent
                        className={cx(
                          globalClasses[headingLevel || 'h4'],
                          classes.heading
                        )}
                      >
                        {heading}
                      </HeadingComponent>
                    )}
                    {block && <RichText content={block} isOnDark={isOnDark} />}
                  </Grid2>
                </GridContainer2>
              );
            } else {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                <div key={i} className={classes.embeddedContent}>
                  <GenericComponent
                    // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                    key={i}
                    content={block.data.target as SectionContent}
                  />
                </div>
              );
            }
          }
        )}
    </div>
  );
}
